import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useApp } from '../../lib/app-context';
import { mad } from '../../lib/format';
import { BarChart, LineChart, type Point } from '../../components/charts';
import { Spinner } from '../../components/ui';
import { t } from '../../lib/i18n';

/**
 * A host's numbers, where they can act on them: views tell them whether the
 * listing is being found, requests whether it convinces, and the per-listing
 * table which one to fix first.
 */

type HostAnalytics = {
  days: number;
  series: { views: Point[]; requests: Point[]; bookings: Point[]; earningsCentimes: Point[] };
  totals: { views: number; requests: number; bookings: number; earningsCentimes: number };
  listings: {
    id: string;
    title: string;
    status: string;
    views: number;
    requests: number;
    bookings: number;
    earningsCentimes: number;
    requestRate: number;
  }[];
};

const TERRA = '#B04E33';
const TEAL = '#1F6F63';
const SAND = '#C9913D';

export default function HostStats() {
  const { reportError } = useApp();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<HostAnalytics | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    api
      .get<HostAnalytics>(`/host/analytics?days=${days}`)
      .then((response) => active && setData(response))
      .catch(reportError);
    return () => {
      active = false;
    };
  }, [days, reportError]);

  return (
    <section className="stack">
      <div className="row row--between row--wrap" style={{ gap: 10 }}>
        <h2 style={{ margin: 0 }}>{t("Statistiques")}</h2>
        <div className="row" style={{ gap: 6 }}>
          {[7, 30, 90].map((option) => (
            <button
              key={option}
              type="button"
              className={`chip ${days === option ? 'chip--on' : ''}`}
              onClick={() => setDays(option)}
            >
              {t('{count} j', { count: option })}
            </button>
          ))}
        </div>
      </div>

      {data === null ? (
        <Spinner />
      ) : (
        <>
          <div className="kpis">
            {[
              [t("Vues"), String(data.totals.views), t('visiteurs uniques')],
              [t("Demandes"), String(data.totals.requests), t("reçues")],
              [t("Réservations"), String(data.totals.bookings), t("confirmées")],
              [t("Revenus"), mad(data.totals.earningsCentimes), t("votre prix, en espèces")],
            ].map(([label, value, hint]) => (
              <div key={label} className="card kpi">
                <span className="small muted">{label}</span>
                <span className="kpi__value">{value}</span>
                <span className="kpi__hint">{hint}</span>
              </div>
            ))}
          </div>

          <div className="card card--pad stack stack--tight">
            <h3 style={{ margin: 0 }}>{t("Vues et demandes")}</h3>
            <LineChart
              series={[
                { label: t("Vues"), color: TEAL, points: data.series.views },
                { label: t("Demandes"), color: SAND, points: data.series.requests },
              ]}
            />
          </div>

          <div className="card card--pad stack stack--tight">
            <h3 style={{ margin: 0 }}>{t("Revenus confirmés")}</h3>
            <BarChart
              points={data.series.earningsCentimes}
              color={TERRA}
              format={(value) => (value >= 100_000 ? `${Math.round(value / 100_000)}k` : mad(value))}
            />
          </div>

          {data.listings.length > 0 ? (
            <div className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Par annonce")}</h3>
              {/* Cards rather than a table, so it reads on a phone. */}
              <div className="stack stack--tight">
                {data.listings.map((listing) => (
                  <div key={listing.id} className="host-stat-row">
                    <strong className="host-stat-row__title">{listing.title}</strong>
                    <span>{listing.views} vues</span>
                    <span>{listing.requests} demandes</span>
                    <span>{listing.bookings} {t("réservations")}</span>
                    <span>{mad(listing.earningsCentimes)}</span>
                    <span className="muted">
                      {listing.views > 0
                        ? t('{percent} % des visiteurs demandent', { percent: Math.round(listing.requestRate * 100) })
                        : t("Pas encore de visites")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
